<template>
  <div
    ref="videoWrapper"
    class="video-wrapper"
    :tabindex="isBackgroundVideo ? -1 : 0"
    :aria-label="altText"
    @mousedown="clickOnVideo()"
    @mouseup="togglePlay"
    @keydown.space.prevent="handleSpaceKey($event)"
  >
    <video
      v-if="isBackgroundVideo"
      ref="player"
      :autoplay="isBackgroundVideo && !isSitecore"
      muted
      :loop="isBackgroundVideo && !isSitecore"
      :playsinline="isBackgroundVideo && !isSitecore"
      :webkit-playsinline="isBackgroundVideo && !isSitecore"
      @play="isPlaying = true"
      @pause="isPlaying = false"
      @timeupdate="timeUpdate"
      @loadedmetadata="metadataUpdate"
    >
      <source
        ref="videoSource"
        :src="src"
        type="video/mp4"
      >
      <track
        v-if="captions"
        label="CC"
        :srclang="lang"
        kind="captions"
        :src="src"
      >
      <track
        v-if="descriptions"
        label="Description"
        :srclang="lang"
        kind="descriptions"
        :src="src"
      >
      {{ notSupportedMsg }}
    </video>
    <video
      v-else
      ref="player"
      @play="isPlaying = true"
      @pause="isPlaying = false"
      @timeupdate="timeUpdate"
      @loadedmetadata="metadataUpdate"
    >
      <source
        ref="videoSource"
        :src="src"
        type="video/mp4"
      >
      <track
        v-if="captions"
        label="CC"
        :srclang="lang"
        kind="captions"
        :src="src"
      >
      <track
        v-if="descriptions"
        label="Description"
        :srclang="lang"
        kind="descriptions"
        :src="src"
      >
      {{ notSupportedMsg }}
    </video>
    <div
      v-if="!isBackgroundVideo || isBackgroundVideo === 0"
      class="ui"
    >
      <div
        v-if="showVideoPoster"
        class="poster"
        :class="{ 'no-poster': noPosterImage }"
        :style="[{ 'background-image': `url(${this.videoPoster})` }]"
      >
        <button
          type="button"
          class="play-icon"
          aria-label="Play"
        >
          <span aria-hidden="true">{{ tc('videoPlayer.play') }}</span>
        </button>
      </div>
      <div
        v-else-if="!isBackgroundVideo || isBackgroundVideo === 0"
        class="controls"
        @click.stop=""
      >
        <div class="buttons">
          <div class="left-section">
            <div class="btn play-pause">
              <button
                type="button"
                ref="playPauseButton"
                :aria-label="isPlaying ? tc('videoPlayer.pause'): tc('videoPlayer.play')"
                @keydown.enter.prevent="togglePlay({ isKeyboard: true })"
                @keydown.space.prevent="togglePlay({ isKeyboard: true })"
              >
                <i
                  v-if="!isPlaying"
                  aria-hidden="true"
                  class="fa fa-play"
                />
                <i
                  v-else
                  aria-hidden="true"
                  class="fa fa-pause"
                />
              </button>
            </div>
            <div class="volume">
              <button
                type="button"
                :aria-label="volume ? tc('videoPlayer.mute'): tc('videoPlayer.unmute')"
                @click.prevent="toggleVolume"
              >
                <i
                  v-if="volume"
                  aria-hidden="true"
                  class="fa fa-volume-up"
                />
                <i
                  v-else
                  aria-hidden="true"
                  class="fa fa-volume-off"
                  style="margin-right: 12px"
                />
              </button>
              <slider
                ref="volume"
                v-model="volume"
                :class="{ 'active': displayVolumeSlider }"
                :aria-label="tc('videoPlayer.volume')"
                slider-type="volume"
                @focus="showVolumeSlider"
                @blur="hideVolumeSlider"
              />
            </div>
          </div>
          <div class="center-section">
            <slider
              ref="timeline"
              v-model="currentTime"
              class="timeline"
              :min="0"
              :max="videoDuration"
              :aria-label="tc('videoPlayer.progressBar')"
              @interact="isSeeking = $event"
            />
            <span class="timestamp">
              {{ timeRemaining }}
            </span>
          </div>
          <div class="right-section">
            <div class="btn full-screen" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="video-controls"
      tabindex="0"
    >
      <div
        v-if="isPlaying"
        class="video-pause"
      >
        <svg
          width="25px"
          height="25px"
          viewBox="0 0 25 25"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <title>{{ tc('videoPlayer.pause') }}</title>
          <g
            id="--Masthead"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
            opacity="1"
          >
            <g
              id="Masthead-Global-homepage-mobile-Copy"
              transform="translate(-331.000000, -488.000000)"
              fill="#FFFFFF"
              fill-rule="nonzero"
            >
              <g
                id="02-Elements/Graphics/Icons/play"
                transform="translate(331.000000, 488.000000)"
              >
                <path
                  id=""
                  d="M12.0555268,24.1110536 C18.7152332,24.1110536 24.1110536,18.7152332 24.1110536,12.0555268 C24.1110536,5.39582047 18.7152332,0 12.0555268,0 C5.39582047,0 0,5.39582047 0,12.0555268 C0,18.7152332 5.39582047,24.1110536 12.0555268,24.1110536 Z M12.0555268,22.7221935 C6.16219348,22.7221935 1.38886015,17.9488601 1.38886015,12.0555268 C1.38886015,6.16219348 6.16219348,1.38886015 12.0555268,1.38886015 C17.9488601,1.38886015 22.7221935,6.16219348 22.7221935,12.0555268 C22.7221935,17.9488601 17.9488601,22.7221935 12.0555268,22.7221935 Z M9,6.22222222 L9.66666667,6.22222222 C10.2189514,6.22222222 10.6666667,6.66993747 10.6666667,7.22222222 L10.6666667,16.7777778 C10.6666667,17.3300625 10.2189514,17.7777778 9.66666667,17.7777778 L9,17.7777778 C8.44771525,17.7777778 8,17.3300625 8,16.7777778 L8,7.22222222 C8,6.66993747 8.44771525,6.22222222 9,6.22222222 Z M14.3333333,6.22222222 L15,6.22222222 C15.5522847,6.22222222 16,6.66993747 16,7.22222222 L16,16.7777778 C16,17.3300625 15.5522847,17.7777778 15,17.7777778 L14.3333333,17.7777778 C13.7810486,17.7777778 13.3333333,17.3300625 13.3333333,16.7777778 L13.3333333,7.22222222 C13.3333333,6.66993747 13.7810486,6.22222222 14.3333333,6.22222222 Z"
                />
              </g>
            </g>
          </g>
        </svg>
      </div>
      <div
        v-if="!isPlaying"
        class="video-play"
      >
        <svg
          width="25px"
          height="26px"
          viewBox="0 0 25 26"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <title>{{ tc('videoPlayer.play') }}</title>
          <g
            id="--Masthead"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
          >
            <g
              id="Masthead-Property-homepage"
              transform="translate(-1396.000000, -583.000000)"
              fill="#FFFFFF"
              fill-rule="nonzero"
            >
              <g
                id="02-Elements/Graphics/Icons/play-Copy"
                transform="translate(1396.437499, 583.937557)"
              >
                <path
                  id=""
                  d="M12.0555268,24.1110536 C18.7152332,24.1110536 24.1110536,18.7152332 24.1110536,12.0555268 C24.1110536,5.39582047 18.7152332,0 12.0555268,0 C5.39582047,0 0,5.39582047 0,12.0555268 C0,18.7152332 5.39582047,24.1110536 12.0555268,24.1110536 Z M12.0555268,22.7221935 C6.16219348,22.7221935 1.38886015,17.9488601 1.38886015,12.0555268 C1.38886015,6.16219348 6.16219348,1.38886015 12.0555268,1.38886015 C17.9488601,1.38886015 22.7221935,6.16219348 22.7221935,12.0555268 C22.7221935,17.9488601 17.9488601,22.7221935 12.0555268,22.7221935 Z M10.0347332,17.6966078 L17.600879,13.138829 C18.3058811,12.7281953 18.3058811,11.6586878 17.600879,11.243515 L10.0347332,6.41497704 C9.35550807,6.01787245 8.50000093,6.52779335 8.50000093,7.36263403 L8.50000093,16.7489508 C8.50000093,17.5792965 9.35118393,18.0937124 10.0347332,17.6966078 Z"
                />
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { intersectionViewportObserver } from '@utils/intersectionViewportObserver'
  import Slider from '@components/01.base/slider/slider.vue'

  export default {
    name: 'VideoPlayer',
    components: { Slider },
    props: {
      desktopVideo: {
        type: String,
        required: true
      },
      desktopVideoPosterImage: {
        type: String,
        default: ''
      },
      mobileVideo: {
        type: String
      },
      mobileVideoPosterImage: {
        type: String
      },
      captions: {
        type: String
      },
      descriptions: {
        type: String
      },
      dictionary: {
        type: Object,
        default: () => ({})
      },
      altText: {
        type: String
      },
      notSupportedMsg: {
        type: String,
        default: 'Not supported'
      },
      isBackgroundVideo: {
        type: Boolean
      },
      disabledToggle: {
        type: Boolean
      },
      pauseControl: {
        type: Boolean
      },
      pauseOnHide: {
        type: Boolean,
        default: true
      },
    },
    data: () => ({
      isPlaying: false,
      isSeeking: false,
      wasPlayingBeforeSeek: false,
      videoDuration: 0,
      currentTime: 0,
      volume: 1,
      internalVolume: 1,
      isFirstTimePlaying: true,
      clickStart: 0,
      clickEnd: 0,
      clickDuration: 0,
      emptyScene7ImageSrc: 'https://photos.mandarinoriental.com/is/image',
      emptyScene7MediaSrc: 'https://photos.mandarinoriental.com/is/content',
      noPosterImage: false,
      displayVolumeSlider: false,
    }),
    computed: {
      ...mapState('responsive', ['breakpoints', 'width']),
      ...mapState('global', ['isSitecore']),
      ...mapGetters('i18n', ['tc']),
      isMobile() {
        return this.width <= 767
      },
      src() {
        return (this.isMobile && (this.mobileVideo && this.mobileVideo !== this.emptyScene7MediaSrc)) ? this.mobileVideo : this.desktopVideo
      },
      videoPoster() {
        if (this.isMobile && this.mobileVideoPosterImage) {
          return this.mobileVideoPosterImage
        }
        if (this.desktopVideoPosterImage !== '') {
          return this.desktopVideoPosterImage
        }
        this.noPosterImage = true
      },
      progress() {
        return (this.currentTime * 100) / this.videoDuration
      },
      timeRemaining() {
        const remainingInMs = this.videoDuration * 1000 - this.currentTime * 1000
        const minutes = Math.floor(remainingInMs / 60000)
        const seconds = ((remainingInMs % 60000) / 1000).toFixed(0)
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
      },
      showVideoPoster() {
        return !!(
          this.isFirstTimePlaying
          || (this.currentTime === this.videoDuration && !this.isPlaying)
        )
      }
    },
    watch: {
      isPlaying(val) {
        if (val) {
          this.$emit('play')
        } else {
          this.$emit('pause')
        }
      },
      currentTime(val) {
        if (this.isSeeking) {
          this.$refs.player.currentTime = val
        }
      },
      volume(val) {
        this.$refs.player.volume = val
        localStorage.volume = val
      },
      src(val) {
        if (val) {
          this.$refs.videoSource.setAttribute('src', val)
          this.$refs.player.load()
        }
      },
      pauseControl(val) {
        if (val) {
          this.$refs.player.pause()
        }
      }
    },
    mounted() {
      this.$emit('mounted')
      if (this.pauseOnHide && !this.isBackgroundVideo) {
        this.configureIntercectOberver()
      }
    },
    async created() {
      if (this.dictionary) {
        await this.registerScopedLang({ scope: 'videoPlayer', lang: this.dictionary })
      }
    },
    methods: {
      ...mapActions('i18n', ['registerScopedLang']),
      clickOnVideo() {
        this.clickStart = new Date()
      },
      togglePlay({ isKeyboard = false }) {
        if (!isKeyboard) {
          this.clickEnd = new Date()
          this.clickDuration = this.clickEnd - this.clickStart
        }

        if (this.isSitecore || this.disabledToggle) {
          return
        }
        if (this.clickDuration < 150) {
          if (this.$refs.player.paused) {
            this.isFirstTimePlaying = false
            this.$refs.player.play()
          } else {
            this.$refs.player.pause()
          }
        }
      },
      toggleVolume() {
        if (this.volume) {
          this.internalVolume = this.volume
          this.volume = 0
        } else {
          this.volume = this.internalVolume
        }
      },
      timeUpdate() {
        this.currentTime = this.$refs.player ? this.$refs.player.currentTime : 0
      },
      metadataUpdate() {
        this.videoDuration = this.$refs.player ? this.$refs.player.duration : 0
        if (!this.isBackgroundVideo) {
          this.$refs.player.muted = false
          if (localStorage.volume) {
            this.volume = parseFloat(localStorage.volume)
          }
        }
      },
      configureIntercectOberver() {
        intersectionViewportObserver(this.$el, {
          checkIsVisible: false,
          callback: this.onObserve,
          threshold: [0.5]
        })
      },
      onObserve(change) {
        if (!change.isIntersecting && this.isPlaying) {
          this.$refs.player.pause()
        }
      },
      showVolumeSlider() {
        this.displayVolumeSlider = true
      },
      hideVolumeSlider() {
        this.displayVolumeSlider = false
      },
      handleSpaceKey(event) {
        if (event.target === this.$refs.playPauseButton) {
          return
        }
        this.togglePlay({ isKeyboard: true })
      }
    }
  }
</script>
